import React, { Component } from 'react';
import { Button, Card, CardBody, Col, Container, Form, Input, Row, Alert, Label } from 'reactstrap';
import { connect } from 'react-redux';
import * as actions from '../../../actions';
import { reduxForm, InjectedFormProps } from 'redux-form';
import approve from 'approvejs';
import history from '../../../helpers/history';
import queryString from 'query-string';
import validateEmail from '../../../helpers/validateEmail';
import { getTranslationKey } from '../../../modules/utils/helpers';
import type { Notify } from '../../../typings/redux';

interface FieldConfig {
  type: string;
  placeholder: string;
  title: string;
  rules: {
    required?: { message: string };
    email?: { message: string };
  };
}

interface FieldsConfig {
  [key: string]: FieldConfig;
}

const fields: FieldsConfig = {
  first_name: {
    type: 'text',
    placeholder: getTranslationKey('firstname'),
    title: getTranslationKey('firstname'),
    rules: {
      required: { message: getTranslationKey('requiredInput') }
    }
  },
  email: {
    type: 'text',
    placeholder: getTranslationKey('emailPlaceholder'),
    title: getTranslationKey('email'),
    rules: {
      email: {
        message: getTranslationKey('emailErrorMessage')
      }
    }
  },
  password: {
    type: 'password',
    placeholder: getTranslationKey('password'),
    title: getTranslationKey('password'),
    rules: {
      required: { message: getTranslationKey('requiredInput') }
    }
  }
};

interface RegisterState {
  first_name: string;
  password: string;
  email: string;
  discountKey?: string;
  affiliate?: string;
  trialWeeks?: string;
}

interface RegisterFormValues {
  first_name?: string;
  email?: string;
  password?: string;
}

interface AuthState {
  authenticated?: boolean;
  [key: string]: any;
}

interface RegisterProps {
  auth: AuthState;
  notify: Notify;
  initialValues: RegisterFormValues;
  _initialValues: RegisterFormValues;
  registerUser: (user: any, notify: Notify) => void;
  initialize: (values: RegisterFormValues) => void;
}

type CombinedProps = RegisterProps & InjectedFormProps<RegisterFormValues, RegisterProps>;

class Register extends Component<CombinedProps, RegisterState> {
  discountKey?: string;

  constructor(props: CombinedProps) {
    super(props);

    this.submit = this.submit.bind(this);
    this.onChangeDiscountKey = this.onChangeDiscountKey.bind(this);
    this.onChangeFirstName = this.onChangeFirstName.bind(this);
    this.onChangeEmail = this.onChangeEmail.bind(this);
    this.onChangePassword = this.onChangePassword.bind(this);

    const parsed = queryString.parse(history.location.search);

    this.discountKey = parsed.discountKey as string;

    this.state = {
      first_name: (parsed.first_name as string) || '',
      password: '',
      email: (parsed.email as string) || '',
      discountKey: parsed.discountKey as string,
      affiliate: parsed.affiliate as string,
      trialWeeks: parsed.tw as string
    };
  }

  UNSAFE_componentWillMount() {
    if (
      window.location.hostname.includes('gallerypix') ||
      window.location.hostname.includes('leadsuite')
    ) {
      window.location.href = '/#/login';
    }
  }

  componentDidMount() {
    this.props.initialize(this.props._initialValues);
  }

  onChangeEmail({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ email: value });
  }

  onChangePassword({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ password: value });
  }

  onChangeDiscountKey({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ discountKey: value });
  }

  onChangeFirstName({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ first_name: value });
  }

  submit() {
    const user: any = {
      email: this.state.email,
      first_name: this.state.first_name,
      password: this.state.password
    };

    if (!user.first_name) {
      this.props.notify({
        title: getTranslationKey('error'),
        message: getTranslationKey('completeForm'),
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 5000
      });

      return;
    }

    if (!user.password) {
      this.props.notify({
        title: getTranslationKey('error'),
        message: getTranslationKey('completeForm'),
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 5000
      });

      return;
    }

    if (!user.email || !validateEmail(user.email)) {
      this.props.notify({
        title: getTranslationKey('error'),
        message: getTranslationKey('emailErrorMessage'),
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 5000
      });

      return;
    }

    user.email = user.email.toLowerCase();

    const { affiliate, discountKey, trialWeeks } = this.state;

    if (affiliate || discountKey) {
      user.digistore = {
        affiliate,
        discountKey: discountKey ? discountKey.toUpperCase() : null
      };
    }

    if (trialWeeks) {
      user.trialWeeks = trialWeeks;
    }

    if (window.USER_TYPE) {
      user.type = window.USER_TYPE;
    }

    user.s = 'production';

    this.props.registerUser(user, this.props.notify);
  }

  render() {
    const { discountKey } = this.state;

    return (
      <div
        className="app flex-row align-items-center"
        style={{
          backgroundColor: 'gray',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md="12" lg="6">
              <Card className="mx-4 primary-highlight-shadow">
                <CardBody className="p-4">
                  <Form onSubmit={this.props.handleSubmit(this.submit)}>
                    <h1>{getTranslationKey('register')}</h1>
                    {this.discountKey && (
                      <Alert color="success">
                        {getTranslationKey('discountKeyActivated', { code: this.discountKey })}
                      </Alert>
                    )}
                    {window.USER_TYPE === 'SB' && (
                      <Row>
                        <Col className="m-t-10 m-b-10">
                          <a href="https://api.scrappbook.de/api/auth/facebook">
                            Mit Facebook registrieren
                          </a>
                        </Col>
                      </Row>
                    )}
                    <Label>{getTranslationKey('firstname')}</Label>
                    <Input
                      value={this.state.first_name}
                      onChange={this.onChangeFirstName}
                      placeholder={getTranslationKey('firstname')}
                      className="form-group"
                      type="text"
                    />

                    <Label>{getTranslationKey('email')}</Label>
                    <Input
                      value={this.state.email}
                      onChange={this.onChangeEmail}
                      placeholder={getTranslationKey('emailPlaceholder')}
                      className="form-group"
                      type="text"
                    />

                    <Label>{getTranslationKey('password')}</Label>
                    <Input
                      value={this.state.password}
                      onChange={this.onChangePassword}
                      placeholder={getTranslationKey('password')}
                      className="form-group"
                      type="password"
                    />

                    {this.discountKey && (
                      <div>
                        <Label>{getTranslationKey('pricingTables.discountKey')}</Label>
                        <Input
                          value={discountKey}
                          onChange={this.onChangeDiscountKey}
                          placeholder={getTranslationKey('pricingTables.discountKey')}
                          className="form-group"
                          type="text"
                        />
                      </div>
                    )}
                    <Row>
                      <Col xs="12">
                        <Button color="primary" className="w-100-p" type="submit">
                          {getTranslationKey('createAccount')}
                        </Button>
                      </Col>
                      <Col xs="12" className="text-center">
                        <Button href="/#/login/" color="link" className="px-0 m-b-0">
                          {getTranslationKey('alreadyAccount')}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

interface ValidationErrors {
  [key: string]: string[];
}

function validate(values: RegisterFormValues): ValidationErrors {
  const errors: ValidationErrors = {};

  Object.keys(fields).forEach((fieldKey) => {
    const result = approve.value(
      values[fieldKey as keyof RegisterFormValues],
      fields[fieldKey].rules
    );
    if (result.errors.length > 0) {
      errors[fieldKey] = result.errors;
    }
  });

  return errors;
}

interface RootState {
  auth: AuthState;
  notify: Notify;
}

function mapStateToProps({ auth, notify }: RootState) {
  const initialValues: RegisterFormValues = {};

  try {
    let first_name: string | undefined;
    let email: string | undefined;

    const hashParsed = queryString.parse(window.location.hash.replace('#/register', ''));
    first_name = hashParsed.first_name as string;
    email = hashParsed.email as string;

    if (!first_name && !email) {
      console.log(
        'nothing found in hash - check =>',
        window.location.search.replace('#/register', '')
      );

      const parsed = queryString.parse(window.location.search.replace('#/register', ''));
      first_name = parsed.first_name as string;
      email = parsed.email as string;

      if (!first_name && !email) {
        console.log(
          'nothing found in search - check =>',
          window.location.href.replace('/#/register', '')
        );
        const parsedUrl = queryString.parseUrl(window.location.href.replace('/#/register', ''));

        first_name = parsedUrl.query.first_name as string;
        email = parsedUrl.query.email as string;
      }
    }

    if (first_name) {
      initialValues.first_name = first_name;
    }
    if (email) {
      initialValues.email = email;
    }
  } catch (e) {
    console.log(e);
  }

  return {
    auth,
    notify,
    initialValues,
    _initialValues: initialValues
  };
}

const RegisterWithForm = reduxForm<RegisterFormValues, RegisterProps>({
  form: 'RegisterForm',
  enableReinitialize: true,
  destroyOnUnmount: false,
  validate
})(Register);

const ConnectedRegister = connect(mapStateToProps, actions)(RegisterWithForm);

export default ConnectedRegister;
